import { type FC, useCallback } from "react";
import { Heading, Link } from "@stubhub/broadway-web";
import { Feature, RecordParticipationTypes } from "@viagogo/feature";
import type { CategoryGridItem } from "src/http/types";
import { FormatContent, useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { useCarouselItemInView } from "src/website-apps/shared/components/carousel-module/use-carousel-item-in-view";
import { FAVORITES_BUTTON2_VARIANTS } from "src/website-apps/shared/components/FavoritesV2Buttons/constants";
import { FavoritesButtonWithCount } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/FavoritesButtonWithCount";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { Image, ImageSize } from "src/website-apps/shared/components/image";
import { type PerformerGridVariant, performerGridEntryRecipe } from "./styled-components";

interface Props {
  categoryItem: CategoryGridItem;
  itemIndex: number;
  itemCount: number;
  variant?: PerformerGridVariant;
  categoryId?: number | undefined;
  onAnalyticsClick?: (() => void) | undefined;
  onAnalyticsHover?: (() => void) | undefined;
}

export const AlternativePerformerEntry: FC<Props> = ({
  categoryItem,
  itemIndex,
  itemCount,
  variant = "default",
  categoryId,
  onAnalyticsClick,
  onAnalyticsHover,
}) => {
  const entryStyles = performerGridEntryRecipe({ variant });
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const firePageEvent = useCallback(() => {
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.ALTERNATIVE_PERFORMER_NEW_GRID, String(categoryItem.categoryId));
    onAnalyticsClick?.();
  }, [categoryItem.categoryId, onAnalyticsClick, storePageEvent]);
  const inViewRef = useCarouselItemInView({
    carouselId: CarouselId.AlternativePerformers,
    itemId: categoryItem.categoryId,
    itemIndex,
    itemCount,
    itemType: CarouselItemType.Artist,
    categoryId,
  });
  const FALLBACK_IMAGE_SRC = `${appContext.env["REACT_APP_ASSETS_URL"] ?? ""}/img/cat/2/1/18.jpg`;
  const defaultImageAltText = useContent("cid$#4138909#"); // Performer
  const eventCount = categoryItem.availableLocalEvents ?? 0;
  return (
    <div ref={inViewRef} onMouseEnter={onAnalyticsHover}>
      <Link href={categoryItem.url ?? "#"} onClick={firePageEvent} className={entryStyles.container ?? ""} kind="ghost">
        <Image
          alt={categoryItem.name ?? defaultImageAltText}
          src={categoryItem.imageUrl}
          size={variant === "compact" ? { width: 56, height: 56 } : ImageSize.AlternativePerformerNewGridEntry}
          fallbackImageSrc={FALLBACK_IMAGE_SRC}
          className={entryStyles.image}
        />
        <div className={entryStyles.title}>
          <Heading as="h3">{categoryItem.name}</Heading>
          {eventCount > 0 && (
            <span className={entryStyles.eventCount}>
              {eventCount === 1 ? (
                /* "{0} event near you" */
                <FormatContent id="cid$#3855279#" params={[eventCount]} />
              ) : (
                /* "{0} events near you" */
                <FormatContent id="cid$#3855278#" params={[eventCount]} />
              )}
            </span>
          )}
        </div>
        <div className={entryStyles.favButton}>
          <Feature
            id={FeatureSetEnum.TMPerformerArtistCarouselHideFollowCount}
            recordType={RecordParticipationTypes.IN_VIEW}
            canParticipate={() => (categoryItem.aggregateFavorites ?? 0) > 0}
            control={() => (
              <FavoritesButtonWithCount
                stopPropagation
                preventDefault
                variant={FAVORITES_BUTTON2_VARIANTS.LIGHT}
                entityType={ENTITY_TYPES.CATEGORY}
                entityId={categoryItem.categoryId}
                aggregateFavorites={categoryItem.aggregateFavorites}
                favoritePreviewSummary={{ name: categoryItem.name ?? null, imageUrl: categoryItem.imageUrl ?? null }}
                enableTooltip
              />
            )}
            variant={() => (
              <FavoritesButtonWithCount
                stopPropagation
                preventDefault
                variant={FAVORITES_BUTTON2_VARIANTS.LIGHT}
                entityType={ENTITY_TYPES.CATEGORY}
                entityId={categoryItem.categoryId}
                aggregateFavorites={categoryItem.aggregateFavorites}
                favoritePreviewSummary={{ name: categoryItem.name ?? null, imageUrl: categoryItem.imageUrl ?? null }}
                hideCount
                enableTooltip
              />
            )}
          />
        </div>
      </Link>
    </div>
  );
};
